import React, { Fragment, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { DownIcon, UpIcon } from '../iconSet/IconSet'
import { CollapsibleMenuPropsTypes, ItemTypes } from './CollapsibleMenuTypesNew'

const CollapsibleMenuSmallNew = (props: CollapsibleMenuPropsTypes) => {
    const location = useLocation()
    const navigate = useNavigate()

    const [activeItem, setActiveItem] = useState<string | null>(null)
    const [hoveredItem, setHoveredItem] = useState<string | null>(null)
    const [popoutPosition, setPopoutPosition] = useState<{top: number; left: number}>({top: 0, left: 0})
    const [nestedPopout, setNestedPopout] = useState<{key: string; position: {top: number; left: number}} | null>(null)
    const popoutRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const nestedHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Get URLSearchParams to handle query parameters
    const searchParams = new URLSearchParams(location.search)
    const currentModule = searchParams.get('module')

    // Method 1: Original exact pathname match
    const isItemActiveExact = (item: ItemTypes): boolean => {
        return item.link === location.pathname
    }

    // Method 2: Query parameter based match
    const isItemActiveWithQuery = (item: ItemTypes): boolean => {
        if (!item.link) return false
        
        // For items with query parameters like "/training?module=MOD-00001"
        if (item.link.includes('?')) {
            const [itemPath, itemQuery] = item.link.split('?')
            const itemModule = new URLSearchParams(itemQuery).get('module')
            
            return location.pathname === itemPath && currentModule === itemModule
        }
        
        return false
    }

    // Find active item based on current location (using combined method)
    useEffect(() => {
        const findActiveItem = (items: ItemTypes[]): string | null => {
            for (const item of items) {
                if (isItemActive(item)) {
                    return item.key
                }
                if (item.children) {
                    const childActive = findActiveItem(item.children)
                    if (childActive) {
                        return item.key
                    }
                }
            }
            return null
        }

        const activeKey = findActiveItem(props.items)
        setActiveItem(activeKey)
    }, [location.pathname, location.search, props.items]) // Add location.search dependency

    // Close popup when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popoutRef.current && !popoutRef.current.contains(event.target as Node) &&
                menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setHoveredItem(null)
                setNestedPopout(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current)
            }
            if (nestedHoverTimeoutRef.current) {
                clearTimeout(nestedHoverTimeoutRef.current)
            }
        }
    }, [])

    const handleNavigate = (link: string) => {
        if (link && link !== "#") {
            navigate(link)
            setHoveredItem(null)
            setNestedPopout(null)
        }
    }

    const handleIconMouseEnter = (item: ItemTypes, event: React.MouseEvent) => {
        if (item.children && item.children.length > 0) {
            const rect = event.currentTarget.getBoundingClientRect()
            setPopoutPosition({
                top: rect.top,
                left: rect.right + 8
            })
            setHoveredItem(item.key)
            setNestedPopout(null) // Close any nested popups when opening main one
        }
    }

    const handleIconClick = (item: ItemTypes, event: React.MouseEvent) => {
        event.stopPropagation()
        
        if (!item.children && item.link && item.link !== "#") {
            handleNavigate(item.link)
        }
        if (item.children && item.children.length > 0) {
            setHoveredItem(item.key)
            setNestedPopout(null)
        }
    }

    const handleMouseLeaveMenu = (event: React.MouseEvent) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current)
        }
        
        hoverTimeoutRef.current = setTimeout(() => {
            const relatedTarget = event.relatedTarget as HTMLElement
            if (!popoutRef.current?.contains(relatedTarget)) {
                setHoveredItem(null)
                setNestedPopout(null)
            }
        }, 150)
    }

    const handlePopoutMouseEnter = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current)
        }
    }

    const handlePopoutMouseLeave = (event: React.MouseEvent) => {
        hoverTimeoutRef.current = setTimeout(() => {
            const relatedTarget = event.relatedTarget as HTMLElement
            if (!menuRef.current?.contains(relatedTarget)) {
                setHoveredItem(null)
                setNestedPopout(null)
            }
        }, 150)
    }

    const handleNestedItemMouseEnter = (item: ItemTypes, event: React.MouseEvent) => {
        if (item.children && item.children.length > 0) {
            const rect = event.currentTarget.getBoundingClientRect()
            setNestedPopout({
                key: item.key,
                position: {
                    top: rect.top,
                    left: rect.right + 8
                }
            })
            
            // Clear any nested timeout
            if (nestedHoverTimeoutRef.current) {
                clearTimeout(nestedHoverTimeoutRef.current)
            }
        }
    }

    const handleNestedItemMouseLeave = (event: React.MouseEvent) => {
        if (nestedHoverTimeoutRef.current) {
            clearTimeout(nestedHoverTimeoutRef.current)
        }
        
        nestedHoverTimeoutRef.current = setTimeout(() => {
            const relatedTarget = event.relatedTarget as HTMLElement
            if (!popoutRef.current?.contains(relatedTarget)) {
                setNestedPopout(null)
            }
        }, 150)
    }

    // isItemActive function that uses the combined method
    const isItemActive = (item: ItemTypes): boolean => {
        if (isItemActiveExact(item) || isItemActiveWithQuery(item)) return true
        if (item.children) {
            return item.children.some(child => isItemActive(child))
        }
        return false
    }

    const renderMenuItems = (items: ItemTypes[]) => {
        return items.map((item) => {
            const hasChildren = item.children && item.children.length > 0
            const isActive = isItemActive(item)
            const isHovered = hoveredItem === item.key
            const showPopout = isHovered && hasChildren

            const itemStyle = {
                backgroundColor: isActive 
                    ? 'var(--color-side-menu-sub-item-select-bg)' 
                    : isHovered
                    ? 'var(--color-side-menu-main-item-select-bg)'
                    : 'var(--color-side-menu-main-item-bg)'
            }

            return (
                <Fragment key={item.key}>
                    <div 
                        className={`menu-item-small ${isActive ? 'active' : ''}`}
                        style={itemStyle}
                        onClick={(e) => handleIconClick(item, e)}
                        onMouseEnter={(e) => handleIconMouseEnter(item, e)}
                        onMouseLeave={handleMouseLeaveMenu}
                        title={item.label}
                    >
                        <span className="icon-wrapper-small">
                            {React.cloneElement(item.icons, {
                                color: isActive ? 'var(--color-main-white)' : 'var(--color-text)',
                                width: '1.2rem'
                            })}
                        </span>
                    </div>

                    {showPopout && (
                        <div 
                            ref={popoutRef}
                            className="popout-menu"
                            style={{
                                position: 'fixed',
                                top: `${popoutPosition.top}px`,
                                left: `${popoutPosition.left}px`,
                                zIndex: 1000
                            }}
                            onMouseEnter={handlePopoutMouseEnter}
                            onMouseLeave={handlePopoutMouseLeave}
                        >
                            <div className="popout-header">
                                <span className="popout-title">{item.label}</span>
                            </div>
                            <div className="popout-content">
                                {renderPopoutItems(item.children!, item.key)}
                            </div>
                        </div>
                    )}
                </Fragment>
            )
        })
    }

    const renderPopoutItems = (items: ItemTypes[], parentKey: string) => {
        return items.map((item) => {
            const hasChildren = item.children && item.children.length > 0
            const isActive = isItemActive(item)
            const showNestedPopout = nestedPopout?.key === item.key && hasChildren

            return (
                <Fragment key={item.key}>
                    <div 
                        className={`popout-item ${isActive ? 'active' : ''} ${hasChildren ? 'has-children' : ''}`}
                        onMouseEnter={(e) => handleNestedItemMouseEnter(item, e)}
                        onMouseLeave={handleNestedItemMouseLeave}
                    >
                        <div 
                            className="popout-item-content"
                            onClick={() => {
                                if (!hasChildren && item.link && item.link !== "#") {
                                    handleNavigate(item.link)
                                }
                            }}
                        >
                            <span className="popout-icon">
                                {React.cloneElement(item.icons, {
                                    color: isActive ? 'var(--color-main-white)' : 'var(--color-text)',
                                    width: '1rem'
                                })}
                            </span>
                            <span className="popout-label">{item.label}</span>
                            {hasChildren && (
                                <span className="popout-arrow">›</span>
                            )}
                        </div>
                    </div>

                    {/* Nested popup for child items */}
                    {showNestedPopout && (
                        <div 
                            className="popout-menu nested-popout"
                            style={{
                                position: 'fixed',
                                top: `${nestedPopout.position.top}px`,
                                left: `${nestedPopout.position.left}px`,
                                zIndex: 1001
                            }}
                            onMouseEnter={() => {
                                if (nestedHoverTimeoutRef.current) {
                                    clearTimeout(nestedHoverTimeoutRef.current)
                                }
                            }}
                            onMouseLeave={handleNestedItemMouseLeave}
                        >
                            <div className="popout-header">
                                <span className="popout-title">{item.label}</span>
                            </div>
                            <div className="popout-content">
                                {renderNestedPopoutItems(item.children!)}
                            </div>
                        </div>
                    )}
                </Fragment>
            )
        })
    }

    const renderNestedPopoutItems = (items: ItemTypes[]) => {
        return items.map((item) => {
            const hasChildren = item.children && item.children.length > 0
            const isActive = isItemActive(item)

            return (
                <div 
                    key={item.key}
                    className={`popout-item ${isActive ? 'active' : ''}`}
                    onClick={() => {
                        if (!hasChildren && item.link && item.link !== "#") {
                            handleNavigate(item.link)
                        }
                    }}
                >
                    <div className="popout-item-content">
                        <span className="popout-icon">
                            {React.cloneElement(item.icons, {
                                color: isActive ? 'var(--color-main-white)' : 'var(--color-text)',
                                width: '1rem'
                            })}
                        </span>
                        <span className="popout-label">{item.label}</span>
                    </div>
                </div>
            )
        })
    }

    return (
        <div className={`collapsible-menu-wrapper-small`} ref={menuRef}>
            <div className="menu-items-container-small">
                {renderMenuItems(props.items)}
            </div>
        </div>
    )
}

export default CollapsibleMenuSmallNew